/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger, LogService } from '@epg/angular-base-lib';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreateTask } from '../app/home/task-home/task/task-manager/task-manager.component';
import { TaskAssignmentType } from '../enums/task-assignment-type.enum';
import { TaskStatus } from '../enums/task-status.enum';
import { TaskDto } from '../models/dtos/task.dto';
import { RecordModel } from '../models/record/items/record.model';
import { SearchModel } from '../models/search.model';
import { TaskReferenceModel } from '../models/task-reference.model';
import { TaskDataModel, TaskModel } from '../models/task.model';
import { ApiService } from './api.service';
import { SnackbarService } from './snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  tasksChanged = new Subject<TaskModel[]>();
  tasksSelected = new Subject<TaskModel[]>();
  taskChanged = new Subject<TaskModel>();
  taskDeleted = new Subject<TaskModel>();
  newTaskCreated = new Subject<TaskModel>();
  tasks: TaskModel[] = [];
  selectedTasks: TaskModel[] = [];

  private log: Logger;
  constructor(
    private snackbarService: SnackbarService,
    private apiService: ApiService,
    private logService: LogService
  ) {
    this.log = logService.getLogger(this);
  }

  //#region task

  /**
   * Get Task detail and update it into 'tasks' and emit events
   * @param id id of the task
   */
  getTask(id: string, queryParams: HttpParams = new HttpParams()): Promise<TaskModel> {
    return new Promise((resolve) => {
      this.apiService
        .get(`task/${id}`, queryParams)
        .pipe(
          map((taskDto: TaskDto) => {
            return this.taskDtoToModel(taskDto);
          })
        )
        .subscribe(
          (task) => {
            const index = this.tasks.findIndex((x) => x.taskId === task.taskId);
            if (index !== -1) {
              let tmp = this.tasks.slice();
              tmp.splice(index, 1);
              tmp.splice(index, 0, task);
              this.tasks = tmp;
            }
            this.taskChanged.next(task);
            resolve(task);
          },
          (error) => {
            this.log.error(error);
          }
        );
    });
  }

  closeTask(taskId: string): Promise<any> {
    const body = {
      status: 'CLOSED'
    };

    return new Promise((resolve, reject) => {
      this.apiService.patch(`task/${taskId}/processdata`, body).subscribe(
        () => {
          resolve(true);
        },
        (error) => {
          this.log.error(error);
          reject(error);
        }
      );
    });
  }

  searchTask(
    searchBody: SearchModel,
    newQuery: boolean = false,
    queryParams: HttpParams = new HttpParams()
  ): Promise<{ headers: object; body: TaskModel[] }> {
    return new Promise((resolve, reject) => {
      this.apiService
        .search('search/task', searchBody, queryParams)
        .pipe(
          map((response: HttpResponse<TaskDto[]>) => {
            let headers: any = {};
            if (response.headers.has('X-Total-Count')) {
              headers['X-Total-Count'] = response.headers.get('X-Total-Count');
            }
            if (response.headers.has('X-Per-Page')) {
              headers['X-Per-Page'] = response.headers.get('X-Per-Page');
            }

            let taskDtos: TaskDto[] = response.body ?? [];
            let tasks: TaskModel[] = [];
            for (var taskDto of taskDtos) {
              tasks.push(this.taskDtoToModel(taskDto));
            }

            return {
              headers: headers,
              body: tasks
            };
          })
        )
        .subscribe(
          (response) => {
            let tasks = response.body;

            if (!newQuery) {
              tasks.forEach((task) => {
                if (this.tasks.indexOf(task) === -1) {
                  this.tasks.push(task);
                }
              });
            } else {
              this.tasks = tasks;
            }
            this.tasksChanged.next(this.tasks.slice());
            resolve({
              headers: response.headers,
              body: tasks.slice()
            });
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  selectTasks(
    searchBody: SearchModel,
    startDate: String,
    endDate: String,
    queryParams: HttpParams = new HttpParams()
  ): Promise<{ headers: object; body: TaskModel[] }> {
    return new Promise((resolve) => {
      this.apiService
        .search('search/task', searchBody, queryParams)
        .pipe(
          map((response: HttpResponse<TaskDto[]>) => {
            let headers: any = {};
            if (response.headers.has('X-Total-Count')) {
              headers['X-Total-Count'] = response.headers.get('X-Total-Count');
            }
            if (response.headers.has('X-Per-Page')) {
              headers['X-Per-Page'] = response.headers.get('X-Per-Page');
            }

            let taskDtos: TaskDto[] = response.body ?? [];
            let tasks: TaskModel[] = [];
            for (var taskDto of taskDtos) {
              tasks.push(this.taskDtoToModel(taskDto));
            }

            return {
              headers: headers,
              body: tasks
            };
          })
        )
        .subscribe(
          (response) => {
            let tasks = response.body;

            this.selectedTasks = [];
            for (let task of tasks) {
              if (this.selectedTasks.indexOf(task) === -1) {
                if (task.time_tracking && task.time_tracking.length >= 2) {
                  let start: Date = new Date();
                  let end: Date = new Date();
                  for (let i = 0; i < task.time_tracking?.length!; i++) {
                    let check = task.time_tracking![i];
                    if (check.name === 'start') {
                      start = new Date(check.start);
                    }
                    // else if (check.name === 'end') {
                    //   end = new Date(check.start);
                    // } else if (i + 1 >= task.time_tracking?.length!) {
                    //   end = new Date(check.start);
                    // }
                  }
                  if (start.toISOString() >= startDate && start.toISOString() <= endDate) {
                    this.selectedTasks.push(task);
                  }
                }
              }
            }
            this.tasksSelected.next(this.selectedTasks.slice());
            resolve({
              headers: response.headers,
              body: this.selectedTasks.slice()
            });
          },
          (error) => {
            this.log.error(error);
          }
        );
    });
  }

  async deleteTask(taskId: string) {
    return new Promise((resolve, reject) => {
      this.apiService.delete(`task/${taskId}`).subscribe(
        (task: TaskDto) => {
          let index = this.tasks.findIndex((x) => x.taskId === taskId);

          if (index !== -1) {
            this.tasks.splice(index, 1);
          }

          this.tasksChanged.next(this.tasks.slice());
          this.taskDeleted.next(this.taskDtoToModel(task));
          resolve(true);
        },
        (error) => {
          this.log.error(error);
          reject(error);
        }
      );
    });
  }

  releaseTask(taskId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.apiService.post(`task/${taskId}/releasetask`, null).subscribe(
        () => {
          resolve(true);
        },
        (error) => {
          this.log.error(error);
          reject(error);
        }
      );
    });
  }

  _reassignUserTask(task: TaskModel, employeeId: string) {
    const body = {
      assigned_to: [employeeId],
      assigned_to_employee_or_employeegroup:
        TaskAssignmentType[task.assignedToEmployeeOrEmployeegroup]
    };
    return new Promise((resolve, reject) => {
      this.apiService.patch(`task/${task.taskId}/staticdata`, body).subscribe(
        () => {
          resolve(true);
        },
        (error) => {
          this.log.error(error);
          reject(error);
        }
      );
    });
  }

  _claimTask(taskId: string, employeeId: string) {
    return new Promise((resolve, reject) => {
      this.apiService.post(`task/${taskId}/claimtask/` + employeeId, null).subscribe(
        () => {
          resolve(true);
        },
        (error) => {
          this.log.error(error);
          reject(error);
        }
      );
    });
  }

  async claimTask(task: TaskModel, employeeId: string) {
    // if task is already claimed, release first
    if (task.isClaimed) {
      await this.releaseTask(task.taskId);
    }
    // if user task, re-assign to another employee
    if (!task.groupedTask) {
      await this._reassignUserTask(task, employeeId);
    }
    // claim task
    await this._claimTask(task.taskId, employeeId);
    // refresh task dataaaaaa
    this.getTask(task.taskId);
  }

  taskDtoToModel(taskDto: TaskDto): TaskModel {
    let task = new TaskModel(taskDto.task_id);

    task.taskId = taskDto.task_id;
    task.name = taskDto.name;
    task.description = taskDto.description;
    task.workflowId = taskDto.workflow_id;
    task.workflow_name = taskDto.workflow_name;
    task.assignedTo = taskDto.assigned_to?.slice();
    task.tags = taskDto.tags?.slice();
    task.claimedBy = taskDto.claimed_by;
    task.currentState = taskDto.current_state;
    task.input = taskDto.input;
    task.temp = taskDto.temp;
    task.stack = taskDto.stack?.slice();
    task.executedBy = taskDto.executed_by?.slice();
    task.createdAt = taskDto.createdAt;
    task.updatedAt = taskDto.updatedAt;
    task.scheduled_at = taskDto.scheduled_at;
    task.scheduled_by = taskDto.scheduled_by;
    task.closedBy = taskDto.closed_by;
    task.closedAt = taskDto.closed_at;
    task.time_tracking = taskDto.time_tracking?.slice();
    task.incidents = taskDto.incidents ? [...taskDto.incidents] : undefined;
    task.meta_data = taskDto.meta_data;
    task.tracing = taskDto.tracing;

    switch (taskDto.status?.toString()) {
      case 'NEW':
        task.status = TaskStatus.NEW;
        break;
      case 'IN_PROGRESS':
        task.status = TaskStatus.IN_PROGRESS;
        break;
      case 'CLOSED':
        task.status = TaskStatus.CLOSED;
        break;
    }

    switch (taskDto.assigned_to_employee_or_employeegroup?.toString()) {
      case 'EMPLOYEE':
        task.assignedToEmployeeOrEmployeegroup = TaskAssignmentType.EMPLOYEE;
        break;
      case 'EMPLOYEEGROUP':
        task.assignedToEmployeeOrEmployeegroup = TaskAssignmentType.EMPLOYEEGROUP;
        break;
    }

    task.groupedTask = false;
    if (
      (task.assignedToEmployeeOrEmployeegroup == TaskAssignmentType.EMPLOYEE &&
        task.assignedTo?.length > 1) ||
      task.assignedToEmployeeOrEmployeegroup == TaskAssignmentType.EMPLOYEEGROUP
    ) {
      task.groupedTask = true;
    }

    if (taskDto.claimed_by != null && taskDto.claimed_by != '') {
      task.isClaimed = true;
    } else {
      task.isClaimed = false;
    }

    if (taskDto.output != null && Object.keys(taskDto.output!).length != 0) {
      let taskDataModel = Object.create(TaskDataModel.prototype);
      task.output = taskDataModel.fromJson(taskDto.output);
    }

    if (taskDto.record && taskDto.record.length > 0) {
      task.record = [];
      let recordBaseModel = Object.create(RecordModel.prototype);
      for (let record of taskDto.record) {
        let recordModel = recordBaseModel.fromJson(record);
        if (recordModel) {
          task.record.push(recordModel);
        }
      }
    }

    task.references = [];
    if (taskDto.references) {
      let taskReferenceModel = Object.create(TaskReferenceModel.prototype);
      for (const reference of taskDto.references) {
        task.references.push(taskReferenceModel.fromJson(reference));
      }
    }

    return task;
  }

  createTask(body: CreateTask) {
    return new Promise((resolve, reject) => {
      this.apiService
        .post(`task`, body)
        .pipe(
          map((taskDto: TaskDto) => {
            return this.taskDtoToModel(taskDto);
          })
        )
        .subscribe(
          (task: TaskModel) => {
            // this.log.debug(task);
            // this.tasks.splice(0, 0, task);
            // this.tasksChanged.next(this.tasks.slice());
            this.newTaskCreated.next(task);
            resolve(task);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  //#endregion
}
